package com.checkin.host.ui.properties

import androidx.compose.runtime.*
import com.checkin.host.api.ApiClient
import com.checkin.host.api.ApiException
import com.checkin.host.theme.*
import com.checkin.host.ui.components.CollapsibleFilterPanel
import com.checkin.host.ui.components.FilterIconButton
import com.checkin.host.ui.components.FormLabel
import com.checkin.host.ui.components.TextField
import com.checkin.host.ui.components.markSelected
import com.checkin.host.ui.layout.AlertBanner
import com.checkin.host.ui.layout.rememberIsMobile
import com.checkin.host.util.arrayBufferToByteArray
import com.checkin.host.util.toBase64
import com.checkin.shared.PropertyTimeDefaults
import com.checkin.shared.dto.CreatePropertyRequest
import com.checkin.shared.dto.PropertyDto
import com.checkin.shared.dto.UpdatePropertyRequest
import com.checkin.shared.model.PropertyStatus
import com.checkin.shared.model.PropertyType
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.ref
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.khronos.webgl.ArrayBuffer
import org.w3c.dom.HTMLInputElement
import org.w3c.files.FileReader

private sealed class PropertyFormMode {
    data object Create : PropertyFormMode()
    data class Edit(val property: PropertyDto) : PropertyFormMode()
}

@Composable
fun PropertiesScreen() {
    var properties by remember { mutableStateOf<List<PropertyDto>>(emptyList()) }
    var filterCity by remember { mutableStateOf("") }
    var filterType by remember { mutableStateOf<PropertyType?>(null) }
    var error by remember { mutableStateOf<String?>(null) }
    var loading by remember { mutableStateOf(true) }
    var formMode by remember { mutableStateOf<PropertyFormMode?>(null) }
    var showFilters by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()
    val isMobile = rememberIsMobile()
    val filtersActive = filterCity.isNotBlank() || filterType != null

    fun load() {
        scope.launch {
            loading = true
            error = null
            try {
                properties = ApiClient.listProperties(
                    city = filterCity.takeIf { it.isNotBlank() },
                    type = filterType,
                )
            } catch (e: ApiException) {
                error = e.message
            } finally {
                loading = false
            }
        }
    }

    LaunchedEffect(Unit) { load() }

    formMode?.let { mode ->
        PropertyFormModal(
            mode = mode,
            onClose = { formMode = null },
            onSaved = {
                formMode = null
                load()
            },
        )
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
            gap(12.px)
            flexWrap(FlexWrap.Nowrap)
            marginBottom(if (isMobile) 16.px else 24.px)
        }
    }) {
        Div({ style { flex(1); minWidth(0.px); property("min-width", "0") } }) {
            H1({
                style {
                    fontSize(if (isMobile) 22.px else 26.px)
                    fontWeight("700")
                    color(AppColors.text)
                    margin(0.px)
                    letterSpacing((-0.5).px)
                }
            }) { Text("Hébergements") }
            P({
                style {
                    color(AppColors.muted)
                    margin(6.px, 0.px, 0.px, 0.px)
                    fontSize(14.px)
                    lineHeight("1.5")
                }
            }) { Text("Gérez vos biens locatifs") }
        }
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(8.px)
                flexShrink(0)
            }
        }) {
            FilterIconButton(
                expanded = showFilters,
                active = filtersActive,
                onClick = { showFilters = !showFilters },
            )
            Button({
                style {
                    primaryButton()
                    if (isMobile) {
                        fontSize(13.px)
                        padding(8.px, 12.px)
                        whiteSpace("nowrap")
                    }
                }
                onClick { formMode = PropertyFormMode.Create }
            }) { Text(if (isMobile) "+ Créer" else "+ Créer un hébergement") }
        }
    }
    error?.let { AlertBanner(it, isError = true) }

    if (showFilters) {
        CollapsibleFilterPanel(
            isMobile = isMobile,
            applyLabel = "Appliquer les filtres",
            onApply = { load() },
            onReset = {
                filterCity = ""
                filterType = null
                load()
            },
        ) {
            Div { TextField("Filtrer par ville", filterCity) { filterCity = it } }
            Div {
                FormLabel("Type")
                Select(attrs = {
                    style { inputStyle() }
                    onChange { e ->
                        filterType = e.target.value.takeIf { it.isNotEmpty() }?.let { PropertyType.valueOf(it) }
                    }
                }) {
                    Option(value = "", attrs = { markSelected(filterType == null) }) { Text("Tous") }
                    PropertyType.entries.forEach { t ->
                        Option(value = t.name, attrs = { markSelected(filterType == t) }) {
                            Text(propertyTypeLabel(t))
                        }
                    }
                }
            }
        }
    }

    if (loading) {
        P({ style { color(AppColors.muted) } }) { Text("Chargement de la liste...") }
    } else if (properties.isEmpty()) {
        Div({ style { cardStyle() } }) {
            Text("Aucun hébergement pour l'instant. Cliquez sur « Créer un hébergement » pour commencer.")
        }
    } else {
        properties.forEach { p ->
            PropertyRow(
                property = p,
                onEdit = { formMode = PropertyFormMode.Edit(p) },
                onDeleted = { load() },
            )
        }
    }
}

@Composable
private fun PropertyFormModal(mode: PropertyFormMode, onClose: () -> Unit, onSaved: () -> Unit) {
    val editProperty = (mode as? PropertyFormMode.Edit)?.property
    var name by remember(mode) { mutableStateOf(editProperty?.name ?: "") }
    var address by remember(mode) { mutableStateOf(editProperty?.address ?: "") }
    var city by remember(mode) { mutableStateOf(editProperty?.city ?: "") }
    var checkInTime by remember(mode) {
        mutableStateOf(editProperty?.checkInTime ?: PropertyTimeDefaults.CHECK_IN)
    }
    var checkOutTime by remember(mode) {
        mutableStateOf(editProperty?.checkOutTime ?: PropertyTimeDefaults.CHECK_OUT)
    }
    var type by remember(mode) { mutableStateOf(editProperty?.type ?: PropertyType.APPARTEMENT) }
    var formError by remember { mutableStateOf<String?>(null) }
    var saving by remember { mutableStateOf(false) }
    var photoPreviewUrl by remember(mode) { mutableStateOf<String?>(null) }
    var photoFileName by remember { mutableStateOf<String?>(null) }
    var photoBytes by remember { mutableStateOf<ByteArray?>(null) }
    var photoInputRef by remember { mutableStateOf<HTMLInputElement?>(null) }
    var existingPhotoLoading by remember(mode) { mutableStateOf(editProperty?.photoUrl != null) }
    val scope = rememberCoroutineScope()
    val isMobile = rememberIsMobile()
    val isEdit = mode is PropertyFormMode.Edit

    LaunchedEffect(editProperty?.id) {
        val property = editProperty ?: return@LaunchedEffect
        if (property.photoUrl == null) {
            existingPhotoLoading = false
            return@LaunchedEffect
        }
        existingPhotoLoading = true
        try {
            val bytes = ApiClient.propertyPhoto(property.id)
            photoPreviewUrl = "data:image/jpeg;base64,${bytes.toBase64()}"
        } catch (_: Exception) {
            photoPreviewUrl = null
        } finally {
            existingPhotoLoading = false
        }
    }

    fun clearNewPhoto() {
        photoFileName = null
        photoBytes = null
        photoInputRef?.value = ""
        if (isEdit && editProperty?.photoUrl != null) {
            scope.launch {
                existingPhotoLoading = true
                try {
                    val bytes = ApiClient.propertyPhoto(editProperty.id)
                    photoPreviewUrl = "data:image/jpeg;base64,${bytes.toBase64()}"
                } catch (_: Exception) {
                    photoPreviewUrl = null
                } finally {
                    existingPhotoLoading = false
                }
            }
        } else {
            photoPreviewUrl = null
        }
    }

    fun onPhotoSelected(file: org.w3c.files.File) {
        if (!file.type.startsWith("image/")) {
            formError = "Choisissez une image (JPEG, PNG ou WebP)"
            clearNewPhoto()
            return
        }
        formError = null
        photoFileName = file.name
        val reader = FileReader()
        reader.onload = { _: dynamic ->
            val buffer = reader.result as ArrayBuffer
            val bytes = arrayBufferToByteArray(buffer)
            photoBytes = bytes
            photoPreviewUrl = "data:${file.type};base64,${bytes.toBase64()}"
        }
        reader.readAsArrayBuffer(file)
    }

    fun submit() {
        if (saving) return
        when {
            name.isBlank() -> formError = "Le nom est requis"
            address.isBlank() -> formError = "L'adresse est requise"
            city.isBlank() -> formError = "La ville est requise"
            !checkInTime.matches(Regex("""\d{1,2}:\d{2}""")) -> formError = "Heure d'arrivée invalide (ex. 15:00)"
            !checkOutTime.matches(Regex("""\d{1,2}:\d{2}""")) -> formError = "Heure de départ invalide (ex. 11:00)"
            else -> {
                formError = null
                saving = true
                scope.launch {
                    try {
                        when (mode) {
                            PropertyFormMode.Create -> {
                                ApiClient.createProperty(
                                    CreatePropertyRequest(
                                        name = name,
                                        address = address,
                                        city = city,
                                        type = type,
                                        checkInTime = checkInTime.trim(),
                                        checkOutTime = checkOutTime.trim(),
                                        photoContentBase64 = photoBytes?.toBase64(),
                                        photoFileName = photoFileName,
                                    ),
                                )
                            }
                            is PropertyFormMode.Edit -> {
                                ApiClient.updateProperty(
                                    mode.property.id,
                                    UpdatePropertyRequest(
                                        name = name,
                                        address = address,
                                        city = city,
                                        type = type,
                                        checkInTime = checkInTime.trim(),
                                        checkOutTime = checkOutTime.trim(),
                                        photoContentBase64 = photoBytes?.toBase64(),
                                        photoFileName = photoFileName,
                                    ),
                                )
                            }
                        }
                        onSaved()
                    } catch (e: ApiException) {
                        formError = e.message
                    } finally {
                        saving = false
                    }
                }
            }
        }
    }

    Div({
        style {
            position(Position.Fixed)
            property("inset", "0")
            backgroundColor(Color(AppColors.overlayScrim))
            display(DisplayStyle.Flex)
            alignItems(if (isMobile) AlignItems.FlexEnd else AlignItems.Center)
            justifyContent(JustifyContent.Center)
            property("z-index", "1000")
            padding(if (isMobile) 0.px else 16.px)
        }
        onClick { if (!saving) onClose() }
    }) {
        Div({
            style {
                backgroundColor(AppColors.surface)
                borderRadius(16.px)
                width(100.percent)
                property("max-width", if (isMobile) "100%" else "560px")
                property("max-height", if (isMobile) "92vh" else "90vh")
                overflow("auto")
                border(1.px, LineStyle.Solid, AppColors.border)
            }
            onClick { it.stopPropagation() }
        }) {
            Div({
                style {
                    padding(20.px, 24.px)
                    backgroundColor(AppColors.surfaceHigh)
                    color(AppColors.text)
                    property("border-bottom", "1px solid ${AppColors.border}")
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.Center)
                    gap(12.px)
                }
            }) {
                Div {
                    H2({ style { margin(0.px) } }) {
                        Text(if (isEdit) "Modifier l'hébergement" else "Nouvel hébergement")
                    }
                    P({ style { fontSize(13.px); marginTop(8.px); marginBottom(0.px) } }) {
                        Text(
                            if (isEdit) {
                                "Mettez à jour les informations ou la photo."
                            } else {
                                "La photo est facultative mais recommandée pour le parcours invité."
                            },
                        )
                    }
                }
                Button({
                    style {
                        backgroundColor(Color.transparent)
                        color(AppColors.muted)
                        border(0.px)
                        fontSize(24.px)
                        cursor("pointer")
                        padding(0.px)
                    }
                    if (!saving) onClick { onClose() }
                }) { Text("×") }
            }
            Div({ style { padding(20.px, 24.px) } }) {
                formError?.let { AlertBanner(it, isError = true) }
                Div({
                    style {
                        display(DisplayStyle.Grid)
                        property("grid-template-columns", if (isMobile) "1fr" else "1fr 1fr")
                        gap(12.px)
                        marginBottom(12.px)
                    }
                }) {
                    TextField("Nom", name) { name = it }
                    TextField("Ville", city) { city = it }
                }
                Div({ style { marginBottom(12.px) } }) {
                    TextField("Adresse", address) { address = it }
                }
                Div({
                    style {
                        display(DisplayStyle.Grid)
                        property("grid-template-columns", if (isMobile) "1fr" else "1fr 1fr")
                        gap(12.px)
                        marginBottom(12.px)
                    }
                }) {
                    TextField("Heure d'arrivée", checkInTime) { checkInTime = it }
                    TextField("Heure de départ", checkOutTime) { checkOutTime = it }
                }
                P({ style { color(AppColors.muted); fontSize(13.px); margin(0.px, 0.px, 12.px, 0.px) } }) {
                    Text("Format 24 h (ex. 15:00, 11:00). Utilisées sur le contrat de bail.")
                }
                Div({ style { marginBottom(12.px) } }) {
                    FormLabel("Type")
                    Select(attrs = {
                        style { inputStyle() }
                        onChange { type = PropertyType.valueOf(it.target.value) }
                    }) {
                        PropertyType.entries.forEach { t ->
                            Option(value = t.name, attrs = { markSelected(t == type) }) {
                                Text(propertyTypeLabel(t))
                            }
                        }
                    }
                }
                Div({ style { marginBottom(16.px) } }) {
                    FormLabel("Photo (facultatif)")
                    Input(InputType.File, attrs = {
                        attr("accept", "image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp")
                        style { display(DisplayStyle.None) }
                        ref { element ->
                            photoInputRef = element
                            element.onchange = {
                                val file = element.files?.item(0)
                                if (file == null) clearNewPhoto() else onPhotoSelected(file)
                            }
                            onDispose {
                                element.onchange = null
                                photoInputRef = null
                            }
                        }
                    })
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            gap(12.px)
                            alignItems(AlignItems.Center)
                            flexWrap(FlexWrap.Wrap)
                            marginTop(8.px)
                        }
                    }) {
                        Button({
                            style { secondaryButton() }
                            if (!saving) onClick { photoInputRef?.click() }
                        }) {
                            Text(
                                when {
                                    photoBytes != null -> "Changer la photo"
                                    photoPreviewUrl != null -> "Remplacer la photo"
                                    else -> "Ajouter une photo"
                                },
                            )
                        }
                        if (photoBytes != null) {
                            Button({
                                style { secondaryButton() }
                                if (!saving) onClick { clearNewPhoto() }
                            }) { Text("Annuler la nouvelle photo") }
                        }
                        photoFileName?.let { fileName ->
                            Span({ style { fontSize(13.px); color(AppColors.muted) } }) { Text(fileName) }
                        }
                    }
                    when {
                        existingPhotoLoading -> {
                            P({ style { marginTop(12.px); color(AppColors.muted); fontSize(13.px) } }) {
                                Text("Chargement de la photo…")
                            }
                        }
                        photoPreviewUrl != null -> {
                            Img(src = photoPreviewUrl!!) {
                                style {
                                    marginTop(12.px)
                                    width(100.percent)
                                    property("max-height", "200px")
                                    property("object-fit", "cover")
                                    borderRadius(8.px)
                                    display(DisplayStyle.Block)
                                }
                            }
                        }
                    }
                }
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        gap(8.px)
                        flexWrap(FlexWrap.Wrap)
                        if (isMobile) flexDirection(FlexDirection.Column)
                    }
                }) {
                    Button({
                        style { secondaryButton(); if (isMobile) width(100.percent) }
                        if (!saving) onClick { onClose() }
                    }) { Text("Annuler") }
                    Button({
                        style {
                            primaryButton()
                            if (isMobile) width(100.percent)
                            property("opacity", if (saving) "0.7" else "1")
                            property("cursor", if (saving) "wait" else "pointer")
                        }
                        onClick { submit() }
                    }) {
                        Text(
                            when {
                                saving && isEdit -> "Enregistrement…"
                                saving -> "Création…"
                                isEdit -> "Enregistrer"
                                else -> "Créer"
                            },
                        )
                    }
                }
            }
        }
    }

    DisposableEffect(Unit) {
        val previous = document.body?.asDynamic()?.style?.overflow
        document.body?.asDynamic()?.style?.overflow = "hidden"
        onDispose {
            document.body?.asDynamic()?.style?.overflow = previous ?: ""
        }
    }
}

@Composable
private fun PropertyRow(property: PropertyDto, onEdit: () -> Unit, onDeleted: () -> Unit) {
    val scope = rememberCoroutineScope()
    val isMobile = rememberIsMobile()

    Div({
        style {
            cardStyle()
            marginBottom(12.px)
            display(DisplayStyle.Flex)
            gap(16.px)
            alignItems(AlignItems.Center)
            flexWrap(FlexWrap.Wrap)
            if (isMobile) flexDirection(FlexDirection.Column)
        }
    }) {
        PropertyPhotoThumbnail(propertyId = property.id, hasPhoto = property.photoUrl != null)
        Div({ style { flex(1); minWidth(0.px) } }) {
            Div({ style { fontWeight("600"); fontSize(16.px) } }) { Text(property.name) }
            Div({ style { fontSize(13.px); color(AppColors.muted) } }) {
                Text("${property.address}, ${property.city}")
            }
            Div({ style { fontSize(12.px); color(AppColors.muted); marginTop(4.px) } }) {
                Text(
                    "Arrivée ${property.checkInTime.replace(':', 'h')} · " +
                        "Départ ${property.checkOutTime.replace(':', 'h')}",
                )
            }
            Div({
                style {
                    marginTop(8.px)
                    display(DisplayStyle.Flex)
                    flexWrap(FlexWrap.Wrap)
                    gap(6.px)
                }
            }) {
                Badge(propertyTypeLabel(property.type), AppColors.infoBg, AppColors.info)
                if (property.status == PropertyStatus.INACTIVE) {
                    Badge("Archivé", "rgba(100,116,139,0.15)", AppColors.muted)
                }
                if (property.quotaExcess) {
                    Badge(
                        if (property.autoArchiveScheduled) "Archivage auto prévu" else "Hors quota",
                        AppColors.warningBg,
                        AppColors.warning,
                    )
                }
            }
        }
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(8.px)
                flexWrap(FlexWrap.Wrap)
                if (isMobile) width(100.percent)
            }
        }) {
            if (property.status == PropertyStatus.ACTIVE) {
                Button({
                    style {
                        secondaryButton()
                        if (isMobile) width(100.percent)
                    }
                    onClick { onEdit() }
                }) { Text("Modifier") }
                Button({
                    style {
                        secondaryButton()
                        if (isMobile) width(100.percent)
                    }
                    onClick {
                        if (!window.confirm("Archiver « ${property.name} » ? Vous pourrez le réactiver plus tard.")) return@onClick
                        scope.launch {
                            try {
                                ApiClient.archiveProperty(property.id)
                                onDeleted()
                            } catch (e: ApiException) {
                                window.alert(e.message)
                            }
                        }
                    }
                }) { Text("Archiver") }
            } else {
                Button({
                    style {
                        primaryButton()
                        if (isMobile) width(100.percent)
                    }
                    onClick {
                        scope.launch {
                            try {
                                ApiClient.reactivateProperty(property.id)
                                onDeleted()
                            } catch (e: ApiException) {
                                window.alert(e.message)
                            }
                        }
                    }
                }) { Text("Réactiver") }
            }
            Button({
                style {
                    color(AppColors.danger)
                    backgroundColor(Color.transparent)
                    border(0.px)
                    cursor("pointer")
                    if (isMobile) width(100.percent)
                }
                onClick {
                    if (window.confirm("Supprimer cet hébergement ?")) {
                        scope.launch {
                            try {
                                ApiClient.deleteProperty(property.id)
                                onDeleted()
                            } catch (_: ApiException) {
                            }
                        }
                    }
                }
            }) { Text("Supprimer") }
        }
    }
}

@Composable
private fun PropertyPhotoThumbnail(propertyId: String, hasPhoto: Boolean) {
    var dataUrl by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    LaunchedEffect(propertyId, hasPhoto) {
        if (!hasPhoto) {
            dataUrl = null
            return@LaunchedEffect
        }
        scope.launch {
            try {
                val bytes = ApiClient.propertyPhoto(propertyId)
                dataUrl = "data:image/jpeg;base64,${bytes.toBase64()}"
            } catch (_: Exception) {
                dataUrl = null
            }
        }
    }

    Div({
        style {
            width(96.px)
            height(72.px)
            borderRadius(8.px)
            overflow("hidden")
            backgroundColor(AppColors.surfaceHigh)
            border(1.px, LineStyle.Solid, AppColors.border)
            flexShrink(0)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
        }
    }) {
        if (dataUrl != null) {
            Img(src = dataUrl!!) {
                style {
                    width(100.percent)
                    height(100.percent)
                    property("object-fit", "cover")
                    display(DisplayStyle.Block)
                }
            }
        } else {
            Span({ style { fontSize(11.px); color(AppColors.muted); padding(8.px); textAlign("center") } }) {
                Text(if (hasPhoto) "…" else "Sans photo")
            }
        }
    }
}

private fun propertyTypeLabel(type: PropertyType): String = when (type) {
    PropertyType.APPARTEMENT -> "Appartement"
    PropertyType.VILLA -> "Villa"
    PropertyType.STUDIO -> "Studio"
    PropertyType.MAISON -> "Maison"
    PropertyType.RYAD -> "Ryad"
    PropertyType.AUTRE -> "Autre"
}

@Composable
fun Badge(text: String, bg: String, color: CSSColorValue) {
    Span({
        style {
            display(DisplayStyle.InlineBlock)
            padding(3.px, 9.px)
            borderRadius(20.px)
            fontSize(11.px)
            fontWeight("600")
            backgroundColor(Color(bg))
            color(color)
        }
    }) { Text(text) }
}
